import { FC, ReactNode } from 'react';
import {
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
} from '@radix-ui/react-dialog';
import tw from 'twin.macro';
import styled from 'styled-components';

import Button from './Button';
import { ButtonColor, ButtonSize, Icons } from '../enums';
import Icon from './Icon';
import strings from '../language/Swedish';

const StyledOverlay = styled(Overlay)`
  ${tw`fixed p-4 sm:p-8 inset-0 grid place-items-center overflow-y-scroll bg-transparent backdrop-blur-sm z-50`};
`;

const StyledContent = styled(Content)`
  ${tw`w-full md:max-w-lg sm:w-[90dvw] overflow-hidden m-auto rounded-2xl focus:outline-none p-4 sm:p-8 border border-white bg-white/90 backdrop-blur shadow-2xl`};
`;

const StyledTitle = styled(Title)`
  ${tw`text-slate-800 mb-4 text-lg`};
`;

const StyledDescription = styled(Description)`
  ${tw`text-slate-600 mb-4`};
`;

const CloseButton = styled(Button)`
  ${tw`absolute top-1 right-1 rounded-full`};
`;

const BottomContainer = styled.div`
  ${tw`flex justify-end gap-2 mt-4 sm:mt-8`};

  > * {
    ${tw`flex-1 justify-center`};
  }
`;

interface Props {
  title?: string;
  description?: string;
  bottomButton?: {
    text: string;
    type: 'button' | 'submit' | 'reset' | undefined;
    onClick?: () => void;
  };
  children?: ReactNode;
}

const Dialog: FC<Props> = ({ title, description, bottomButton, children }) => (
  <Portal>
    <StyledOverlay>
      <StyledContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <Close asChild>
          <CloseButton
            color={ButtonColor.transparent}
            size={ButtonSize.large}
            aria-label="Close">
            <Icon icon={Icons.remove} />
          </CloseButton>
        </Close>
        {title && <StyledTitle>{title}</StyledTitle>}
        {description && <StyledDescription>{description}</StyledDescription>}
        {children}
        {bottomButton && (
          <BottomContainer>
            <Close asChild>
              <Button
                type="submit"
                size={ButtonSize.large}
                color={ButtonColor.grey}>
                {strings.dialog.cancel}
              </Button>
            </Close>
            <Close asChild>
              <Button
                type={bottomButton.type}
                color={ButtonColor.primary}
                size={ButtonSize.large}
                onClick={bottomButton.onClick}>
                {bottomButton.text}
              </Button>
            </Close>
          </BottomContainer>
        )}
      </StyledContent>
    </StyledOverlay>
  </Portal>
);

export default Dialog;
